import React, { useState, useEffect } from "react";
import { Box, Flex, Grid } from "theme-ui";
import BasicButton from "components/forms/BasicButton";
import H5 from "components/typography/H5";
import Subtext from "components/typography/Subtext";
import { DndContext, closestCenter, useSensors, useSensor, PointerSensor, DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { themevals } from "theme/themevals";
import Separator from "components/ui/Separator";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "components/ui/Modal";
import { ModalIcon, ModalSubtitle, ModalTitle, ModalTitleWrapper } from "components/ui/Header/styled";
import { QuestionMarkIcon, TrashIcon, PlusIcon, ProductsIcon, ServicesIcon, HandleIcon } from "components/svg";
import SearchField from "components/forms/SearchField";
import { Button } from "theme-ui";
import Checkbox from "components/forms/Checkbox";
import BasicModal from "components/ui/BasicModal";
import P from "components/typography/P";
import SelectInput from "components/forms/SelectInput";
import { useToast } from "components/toast";
import { getInstantEstimateCombos, saveInstantEstimateCombos, updateInstantEstimateCombos, deleteInstantEstimateCombo } from "api/Companies";
import { getEstimateSettings, updateEstimateSettings } from "api/InstantEstimate";
import DepotMinimumsSetting from "./DepotMinimumsSetting";
import { getProducts } from "api/Products";
import { getServices } from "api/Services";

interface DraggableItem {
  id: string;
  name: string;
  position: number;
  service_id?: number;
  product_id?: number;
}

interface ProductCombination {
  id: number;
  bulk_product_id: number;
  bulk_product_name: string;
  related_services: DraggableItem[];
  add_on_products: DraggableItem[];
}

interface InstantEstimateSettingsProps {
  company: any;
  values: any;
  errors: any;
  submitCount: number;
  setFieldValue: (i: string, v: any) => void;
  initCompany: () => Promise<void>;
}

interface ProductModalValues {
  selectedProducts: number[];
}

interface ServiceModalValues {
  selectedServices: number[];
}

interface ComboModalValues {
  bulk_product: number | null;
  selectedServices: number[];
  selectedAddOnProducts: number[];
}

/**
 * Minimum quantity for an instant estimate (yards). Self-contained: own
 * load and own Save, like the Leads tab - the page's "Save Company" does
 * not touch it. Empty = no minimum. Stored in company_data
 * (IE_MIN_QUANTITY_YDS); enforced server-side on create and update.
 */
const MinQuantitySetting: React.FC = () => {
  const toast = useToast();
  const [value, setValue] = useState<string>("");
  const [saved, setSaved] = useState<string>("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    getEstimateSettings().then((s) => {
      const v = s.minQuantityYds ? String(s.minQuantityYds) : "";
      setValue(v);
      setSaved(v);
    });
  }, []);
  const save = async () => {
    setSaving(true);
    const n = value.trim() === "" ? null : Number(value);
    const res: any = await updateEstimateSettings({ minQuantityYds: n });
    setSaving(false);
    if (res?.error) return toast.add(res.error, "var(--theme-ui-colors-danger)");
    const v = res?.minQuantityYds ? String(res.minQuantityYds) : "";
    setValue(v);
    setSaved(v);
    toast.add(v ? `Minimum set to ${v} yd.` : "Minimum removed.", "var(--theme-ui-colors-success)");
  };
  return (
    <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px", marginBottom: "10px" }}>
      <Box>
        <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
          Minimum quantity
        </H5>
        <Subtext marginTop={0} marginBottom={"10px"} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
          The smallest order, in cubic yards, an instant estimate will accept. Leave empty for no minimum.
        </Subtext>
      </Box>
      <Flex sx={{ alignItems: "center", gap: "10px" }}>
        <input
          type="number"
          min={0}
          step={1}
          value={value}
          placeholder="No minimum"
          onChange={(e) => setValue(e.target.value)}
          style={{ width: 140, border: "1px solid #EBEBEB", borderRadius: 6, padding: "8px 10px", fontFamily: "Poppins", fontSize: 13 }}
        />
        <span style={{ fontFamily: "Poppins", fontSize: 13, color: themevals.colors.dkgrey }}>yd</span>
        <BasicButton type="button" keepText disabled={saving || value === saved} onClick={save}>
          {saving ? "Saving\u2026" : "Save"}
        </BasicButton>
      </Flex>
    </Grid>
  );
};

const InstantEstimateSettings: React.FC<InstantEstimateSettingsProps> = ({ company, values, errors, submitCount, setFieldValue, initCompany }) => {
  const [combinations, setCombinations] = useState<ProductCombination[]>(values.data.product_combinations || []);
  const [productOpen, setProductOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [newComboOpen, setNewComboOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedComboId, setSelectedComboId] = useState<number | null>(null);
  const [confirmModal, setConfirmModal] = useState<any>(false);
  const [confirmSubmit, setConfirmSubmit] = useState<boolean>(false);
  const [productsList, setProductsList] = useState<any[]>([]);
  const [servicesList, setServicesList] = useState<any[]>([]);
  const [addOnProductList, setAddOnProductList] = useState<any[]>([]);
  const [bulkProductList, setBulkProductList] = useState<any[]>([]);
  const toast = useToast();

  const _prodValidate = Yup.object().shape({
    selectedProducts: Yup.array().min(1, "Please select at least one product").of(Yup.string()),
  });

  useEffect(() => {
    setFieldValue("data.product_combinations", combinations);
  }, [combinations, setFieldValue]);

  const fetchCombinations = async () => {
    const comboRes = await getInstantEstimateCombos();
    const formattedCombinations = comboRes.items.map((item: any) => ({
      id: item.id,
      bulk_product_id: item.product?.id,
      bulk_product_name: item.product?.name || item.name,
      related_services: item.services
        ? item.services.map((service: any, index: number) => ({
            id: `rs-${service.id}-${index}`,
            service_id: service.serviceId,
            name: service.name,
            position: service.position,
          }))
        : [],
      add_on_products: item.addons
        ? item.addons.map((addon: any, index: number) => ({
            id: `ap-${addon.id}-${index}`,
            product_id: addon.addonId,
            name: addon.name,
            position: addon.position,
          }))
        : [],
    }));
    setCombinations(formattedCombinations);
  };

  const fetchProducts = async () => {
    const products = await getProducts({});
    const bulkProducts = await getProducts({ query: { filters: { addon: "1:isnot" } } });
    const addOnProducts = await getProducts({ query: { filters: { addon: "1:is" } } });
    const services = await getServices({});

    const bulkProductsOptions = bulkProducts.items.map((p: any) => ({ value: p.id, label: p.name }));

    setProductsList(products.items);
    setBulkProductList(bulkProductsOptions);
    setAddOnProductList(addOnProducts.items);
    setServicesList(services.items);
  };

  useEffect(() => {
    fetchCombinations();
    fetchProducts();
  }, []);

  const DraggableItem: React.FC<{ item: DraggableItem; handleRemoveItem: (id: string) => void }> = ({ item, handleRemoveItem }) => {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: item.id });

    const style: React.CSSProperties = {
      transform: CSS.Transform.toString(transform),
      transition,
      padding: "10px",
      border: "1px solid #EBEBEB",
      borderRadius: "6px",
      margin: "10px 0",
      cursor: "grab",
      background: "#fff",
      display: "flex",
      alignItems: "center",
    };

    return (
      <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
        <Box sx={{ display: "flex", alignItems: "center", flex: "1" }}>
          <HandleIcon fill={"#818181"} height={"20px"} />
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} fontSize={"14px"} marginBottom={"0px"} marginTop={"0px"} marginLeft={"15px"}>
            {item.name}
          </H5>
        </Box>
        <Box sx={{ flex: "1", display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
          <div
            style={{ cursor: "pointer", padding: "5px", pointerEvents: "auto" }}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveItem(item.id);
            }}
          >
            <TrashIcon fill={themevals.colors.dkgrey} width="14px" />
          </div>
        </Box>
      </div>
    );
  };

  const DroppableList: React.FC<{
    id: string;
    items: DraggableItem[];
    setItems: React.Dispatch<React.SetStateAction<DraggableItem[]>>;
    comboId: number;
  }> = ({ id, items, setItems, comboId }) => {
    const handleRemoveItem = (comboId: number, itemId: string, type: "related-services" | "add-on-products") => {
      setCombinations((prevCombinations) => {
        const updatedCombinations = prevCombinations.map((combo) =>
          combo.id === comboId
            ? {
                ...combo,
                [type === "related-services" ? "related_services" : "add_on_products"]: combo[type === "related-services" ? "related_services" : "add_on_products"].filter(
                  (item) => item.id !== itemId
                ),
              }
            : combo
        );

        const _updateInsantEstimateCombos = async ({ data }: any) => {
          const newData = {
            productId: data.bulk_product_id,
            name: data.bulk_product_name,
            services: data.related_services.map((service: any) => ({
              serviceId: service.service_id,
              name: service.name,
              position: service.position,
            })),
            addons: data.add_on_products.map((product: any) => ({
              addonId: product.product_id,
              name: product.name,
              position: product.position,
            })),
          };
          const res = await updateInstantEstimateCombos({ data: comboId, update: newData });
          toast.add("Item removed.", "var(--theme-ui-colors-success)");
        };

        const comboIndex = updatedCombinations.findIndex((combo) => combo.id === comboId);

        _updateInsantEstimateCombos({ data: updatedCombinations[comboIndex] });

        setFieldValue("data.product_combinations", updatedCombinations);

        return updatedCombinations;
      });
    };

    return (
      <SortableContext items={items.map((item) => item.id)} strategy={verticalListSortingStrategy}>
        <div
          style={{
            minHeight: "100px",
            width: "100%",
            padding: "20px",
            borderTop: "1px solid #EBEBEB",
          }}
        >
          <Flex sx={{ marginBottom: "30px", alignItems: "flex-end" }}>
            <Box sx={{ flex: "1" }}>
              <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} fontSize={"12px"} marginBottom={"0px"}>
                {id === "related-services" ? "Related Services" : "Add-On Products"}
              </H5>
              <Subtext marginTop={0} marginBottom={0} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
                Drag to reorder for sorting in display.
              </Subtext>
            </Box>
            <Box sx={{ flex: "1", display: "flex", justifyContent: "flex-end" }}>
              {id === "related-services" && (
                <BasicButton scheme="secondaryOutline" type="button" onClick={() => toggleServiceModal(comboId)}>
                  <PlusIcon /> Add services
                </BasicButton>
              )}
              {id === "add-on-products" && (
                <BasicButton scheme="secondaryOutline" type="button" onClick={() => toggleProductModal(comboId)}>
                  <PlusIcon /> Add products
                </BasicButton>
              )}
            </Box>
          </Flex>
          {items
            .sort((a, b) => a.position - b.position)
            .map((item) => (
              <DraggableItem key={item.id} item={item} handleRemoveItem={() => handleRemoveItem(comboId, item.id, id as "related-services" | "add-on-products")} />
            ))}
        </div>
      </SortableContext>
    );
  };

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event: DragEndEvent, comboId: number): void => {
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);

    const updatedCombinations = [...combinations];
    const comboIndex = updatedCombinations.findIndex((combo) => combo.id === comboId);
    if (comboIndex === -1) return;

    const activeContainer = activeId.startsWith("rs-") ? "related-services" : "add-on-products";
    const overContainer = overId.startsWith("rs-") ? "related-services" : "add-on-products";

    if (activeContainer !== overContainer) return;

    const items = activeContainer === "related-services" ? updatedCombinations[comboIndex].related_services : updatedCombinations[comboIndex].add_on_products;

    const oldIndex = items.findIndex((item) => item.id === activeId);
    const newIndex = items.findIndex((item) => item.id === overId);

    if (oldIndex !== -1 && newIndex !== -1) {
      const reorderedItems = arrayMove(items, oldIndex, newIndex).map((item, index) => ({ ...item, position: index }));

      if (activeContainer === "related-services") {
        updatedCombinations[comboIndex].related_services = reorderedItems;
      } else {
        updatedCombinations[comboIndex].add_on_products = reorderedItems;
      }

      const _updateInsantEstimateCombos = async ({ data }: any) => {
        const newData = {
          productId: data.bulk_product_id,
          name: data.bulk_product_name,
          services: data.related_services.map((service: any) => ({
            serviceId: service.service_id,
            position: service.position,
          })),
          addons: data.add_on_products.map((product: any) => ({
            addonId: product.product_id,
            position: product.position,
          })),
        };

        const res = await updateInstantEstimateCombos({ data: comboId, update: newData });
      };

      _updateInsantEstimateCombos({ data: updatedCombinations[comboIndex] });
      setCombinations(updatedCombinations);
      setFieldValue("data.product_combinations", updatedCombinations);
    }
  };

  const handleSetItems = (comboId: number, type: "related-services" | "add-on-products", newItems: DraggableItem[] | ((prev: DraggableItem[]) => DraggableItem[])) => {
    setCombinations((prevCombinations) =>
      prevCombinations.map((combo) =>
        combo.id === comboId
          ? {
              ...combo,
              [type === "related-services" ? "related_services" : "add_on_products"]:
                typeof newItems === "function" ? newItems(combo[type === "related-services" ? "related_services" : "add_on_products"]) : newItems,
            }
          : combo
      )
    );
  };

  const toggleProductModal = (comboId?: number) => {
    setProductOpen(!productOpen);
    setSearchTerm("");
    if (comboId !== undefined) setSelectedComboId(comboId);
  };

  const toggleServiceModal = (comboId?: number) => {
    setServiceOpen(!serviceOpen);
    setSearchTerm("");
    if (comboId !== undefined) setSelectedComboId(comboId);
  };

  const toggleNewComboModal = () => {
    setNewComboOpen(!newComboOpen);
    setSearchTerm("");
  };

  const submitProducts = async (values: ProductModalValues) => {
    if (selectedComboId === null) return;

    setCombinations((prevCombinations) => {
      const updatedCombinations = prevCombinations.map((combo) =>
        combo.id === selectedComboId
          ? {
              ...combo,
              add_on_products: [
                ...combo.add_on_products,
                ...values.selectedProducts.map((productId) => ({
                  id: `ap-${productId}-${Date.now()}`,
                  product_id: productId,
                  name: productsList.find((p) => p.id === productId)?.name || "Unknown Product",
                  position: combo.add_on_products.length,
                })),
              ],
            }
          : combo
      );

      const _updateInsantEstimateCombos = async ({ data }: any) => {
        const newData = {
          productId: data.bulk_product_id,
          name: data.bulk_product_name,
          services: data.related_services.map((service: any) => ({
            serviceId: service.service_id,
            name: service.name,
            position: service.position,
          })),
          addons: data.add_on_products.map((product: any) => ({
            addonId: product.product_id,
            name: product.name,
            position: product.position,
          })),
        };

        const res = await updateInstantEstimateCombos({ data: selectedComboId, update: newData });
      };
      const comboIndex = updatedCombinations.findIndex((combo) => combo.id === selectedComboId);
      _updateInsantEstimateCombos({ data: updatedCombinations[comboIndex] });

      setFieldValue("data.product_combinations", updatedCombinations);

      return updatedCombinations;
    });
    toast.add("Product Added.", "var(--theme-ui-colors-success)");
    setProductOpen(false);
  };

  const submitServices = async (values: ServiceModalValues) => {
    if (selectedComboId === null) return;

    setCombinations((prevCombinations) => {
      const updatedCombinations = prevCombinations.map((combo) =>
        combo.id === selectedComboId
          ? {
              ...combo,
              related_services: [
                ...combo.related_services,
                ...values.selectedServices.map((serviceId) => ({
                  id: `rs-${serviceId}-${Date.now()}`,
                  service_id: serviceId,
                  name: servicesList.find((s) => s.id === serviceId)?.name || "Unknown Service",
                  position: combo.related_services.length,
                })),
              ],
            }
          : combo
      );
      const _updateInsantEstimateCombos = async ({ data }: any) => {
        const newData = {
          productId: data.bulk_product_id,
          name: data.bulk_product_name,
          services: data.related_services.map((service: any) => ({
            serviceId: service.service_id,
            name: service.name,
            position: service.position,
          })),
          addons: data.add_on_products.map((product: any) => ({
            addonId: product.product_id,
            name: product.name,
            position: product.position,
          })),
        };

        const res = await updateInstantEstimateCombos({ data: selectedComboId, update: newData });
      };
      const comboIndex = updatedCombinations.findIndex((combo) => combo.id === selectedComboId);
      _updateInsantEstimateCombos({ data: updatedCombinations[comboIndex] });

      setFieldValue("data.product_combinations", updatedCombinations);

      return updatedCombinations;
    });

    toast.add("Service added.", "var(--theme-ui-colors-success)");
    setServiceOpen(false);
  };

  const comboSchema = Yup.object().shape({
    bulk_product: Yup.string().required("Bulk product is required."),
    selectedServices: Yup.array().min(1, "Please select at least one related service.").of(Yup.string()),
  });

  const submitNewCombo = async (values: ComboModalValues) => {
    const bulkProduct = bulkProductList.find((p) => p.value === values.bulk_product);
    if (!bulkProduct) return;

    const relatedServices = values.selectedServices.map((serviceId, index) => ({
      id: `rs-${serviceId}-${Date.now()}`,
      service_id: serviceId,
      name: servicesList.find((s) => s.id === serviceId)?.name || "Unknown Service",
      position: index,
    }));

    const addOnProducts = values.selectedAddOnProducts.map((productId, index) => ({
      id: `ap-${productId}-${Date.now()}`,
      product_id: productId,
      name: addOnProductList.find((p) => p.id === productId)?.name || "Unknown Product",
      position: index,
    }));

    const newCombo: ProductCombination = {
      id: Date.now(),
      bulk_product_id: values.bulk_product ?? 0,
      bulk_product_name: bulkProduct.label,
      related_services: relatedServices,
      add_on_products: addOnProducts,
    };

    const saveLocalInstantEstimateCombos = async (data: ProductCombination) => {
      const newData = {
        productId: data.bulk_product_id,
        name: data.bulk_product_name,
        services: data.related_services.map((service) => ({
          serviceId: service.service_id,
          name: service.name,
          position: service.position,
        })),
        addons: data.add_on_products.map((product) => ({
          addonId: product.product_id,
          name: product.name,
          position: product.position,
        })),
      };

      const res = await saveInstantEstimateCombos({ data: newData });

      //format response for drag and drop

      return [
        {
          id: res.id,
          bulk_product_id: res.product?.id,
          bulk_product_name: res.product?.name || res.name,
          related_services: res.services
            ? res.services.map((service: any, index: number) => ({
                id: `rs-${service.id}-${index}`,
                service_id: service.serviceId,
                name: service.name,
                position: service.position,
              }))
            : [],
          add_on_products: res.addons
            ? res.addons.map((addon: any, index: number) => ({
                id: `ap-${addon.id}-${index}`,
                product_id: addon.addonId,
                name: addon.name,
                position: addon.position,
              }))
            : [],
        },
      ];
    };

    try {
      const savedCombinations = await saveLocalInstantEstimateCombos(newCombo);

      setCombinations((prevCombinations) => {
        const updatedCombinations = [...prevCombinations, ...savedCombinations];
        setFieldValue("data.product_combinations", updatedCombinations);
        return updatedCombinations;
      });

      toast.add("New product combination added.", "var(--theme-ui-colors-success)");
    } catch (error) {
      console.error("Error saving product combination:", error);
      toast.add("Failed to save product combination.", "var(--theme-ui-colors-danger)");
    }

    setNewComboOpen(false);
  };

  const submitRemove = async (comboId: number) => {
    setConfirmModal(
      <BasicModal
        title="Remove combination?"
        size={"sm"}
        cancel_text="No, cancel"
        cancel_click={() => setConfirmModal(false)}
        icon={<QuestionMarkIcon fill={"#818181"} height="30px" />}
        actions={
          <BasicButton type="button" scheme="danger" onClick={() => removeCombo(comboId)} $submitting={confirmSubmit} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
            {!confirmSubmit && <TrashIcon fill={"#fff"} />}
            {"Remove"}
          </BasicButton>
        }
      >
        <P lineHeight={"22px"}>Are you sure you want to remove this product combination?</P>
      </BasicModal>
    );
  };

  const removeCombo = (comboId: number) => {
    setCombinations((prevCombinations) => {
      const updatedCombinations = prevCombinations.filter((combo) => combo.id !== comboId);
      setFieldValue("data.product_combinations", updatedCombinations);
      const res = deleteInstantEstimateCombo({ data: comboId });
      setConfirmModal(false);
      toast.add("Product combination deleted.", "var(--theme-ui-colors-success)");
      return updatedCombinations;
    });
  };

  return (
    <>
      <Box sx={{ paddingLeft: "30px", paddingRight: "30px", marginTop:"-15px" }}>
        <H5 fontSize={"16px"} fontWeight={"600"} fontFamily={"Poppins"}>
          Instant Estimate
        </H5>
      </Box>
      <Separator />
      <MinQuantitySetting />
      <Separator />
      <DepotMinimumsSetting />
      <Separator />
      <Grid gap={6} columns={[1, "1fr 3fr"]} sx={{ paddingLeft: "30px", paddingRight: "30px" }}>
        <Box>
          <H5 fontFamily={"Poppins"} fontWeight={"600"} color={"#000"} marginTop={"0px"} marginBottom={"5px"}>
            Product Combinations
          </H5>
          <Subtext marginTop={0} marginBottom={"30px"} fontSize={"12px"} fontWeight={"400"} fontFamily={"Poppins"}>
            Edit the product combinations in the Instant Estimate tool.
          </Subtext>
          <BasicButton type="button" $iconHeight="10px" scheme="secondaryOutline" onClick={() => toggleNewComboModal()}>
            <PlusIcon fill="#000" />
            Add combination
          </BasicButton>
        </Box>
        <Box>
          {combinations.length !== 0 &&
            combinations.map((combo) => (
              <Flex key={combo.id} sx={{ border: "1px solid #EBEBEB", borderRadius: "6px", flexDirection: "column", marginBottom: "20px" }}>
                <Flex sx={{ flexDirection: "row", padding: "20px", alignItems: "center" }}>
                  <Box sx={{ flex: "1", display: "flex", alignItems: "center" }}>
                    <H5 fontSize="14px" marginBottom={"0px"} marginTop="0px" fontFamily="Poppins" fontWeight="600">
                      {combo.bulk_product_name}
                    </H5>
                    <Subtext marginBottom={"0px"} marginTop="0px" fontFamily="Poppins" marginLeft="5px">
                      {" "}
                      - Bulk Product
                    </Subtext>
                  </Box>
                  <BasicButton scheme="dangerSecondary" type="button" onClick={() => submitRemove(combo.id)}>
                    <TrashIcon fill={themevals.colors.danger} /> Remove
                  </BasicButton>
                </Flex>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={(event) => handleDragEnd(event, combo.id)}>
                  <DroppableList id="related-services" items={combo.related_services} setItems={(newItems) => handleSetItems(combo.id, "related-services", newItems)} comboId={combo.id} />

                  <DroppableList id="add-on-products" items={combo.add_on_products} setItems={(newItems) => handleSetItems(combo.id, "add-on-products", newItems)} comboId={combo.id} />
                </DndContext>
              </Flex>
            ))}
          {combinations.length === 0 && (
            <P fontFamily={"Poppins"} fontSize={"14px"} fontWeight={"400"} color={themevals.colors.dkgrey} marginBottom="0px" marginTop="0px">
              There are currently no product combinations set for Instant Estimate
            </P>
          )}
        </Box>
      </Grid>
      {confirmModal}
      <Modal isOpen={productOpen} toggle={() => toggleProductModal()} centered size={"sm"}>
        <Formik<ProductModalValues> initialValues={{ selectedProducts: [] }} onSubmit={submitProducts} enableReinitialize validationSchema={_prodValidate}>
          {({
            isSubmitting,
            values,
            setFieldValue,
            submitCount,
            errors,
          }: {
            isSubmitting: boolean;
            values: any;
            setFieldValue: (name: string, val?: any) => void;
            submitCount: number;
            errors: any;
          }) => {
            const toggleProduct = (productId: number) => {
              const newSelected: number[] = values.selectedProducts.includes(productId) ? values.selectedProducts.filter((id: number) => id !== productId) : [...values.selectedProducts, productId];
              setFieldValue("selectedProducts", newSelected);
            };

            const toggleSelectAll = () => {
              if (values.selectedProducts.length === filteredProducts.length) {
                setFieldValue("selectedProducts", []);
              } else {
                setFieldValue(
                  "selectedProducts",
                  filteredProducts.map((p) => p.id)
                );
              }
            };

            const filteredProducts = React.useMemo(() => {
              const currentCombo = combinations.find((combo) => combo.id === selectedComboId);
              if (!currentCombo) return productsList;

              const addedProductIds = new Set(currentCombo.add_on_products.map((p) => p.product_id));

              return productsList.filter((product) => !addedProductIds.has(product.id) && product.name.toLowerCase().includes(searchTerm.toLowerCase()));
            }, [selectedComboId, combinations, searchTerm]);

            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => toggleProductModal()}>
                  <ModalIcon>
                    <ProductsIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Add products</ModalTitle>
                    <ModalSubtitle>{"Add available products at this depot."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                  <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center" }}>
                    <Checkbox label="Select All" checked={values.selectedProducts.length === filteredProducts.length && filteredProducts.length > 0} onChange={toggleSelectAll} />
                  </Flex>
                  {filteredProducts.map((product, idx) => (
                    <Flex
                      key={product.id}
                      sx={{
                        gap: "1rem",
                        alignItems: "center",
                        marginBottom: "10px",
                        border: `1px solid ${values.selectedProducts.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                        borderRadius: "6px",
                        height: "38px",
                        padding: "0px 10px",
                      }}
                    >
                      <Checkbox label={product.name} checked={values.selectedProducts.includes(product.id)} onChange={() => toggleProduct(product.id)} labelSize="14px" />
                    </Flex>
                  ))}
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondaryOutline" type="button" onClick={() => setProductOpen(false)} style={{ marginRight: "10px" }}>
                    Cancel
                  </Button>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <PlusIcon fill={"#fff"} />}
                    Add products
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={serviceOpen} toggle={() => toggleServiceModal()} centered size={"sm"}>
        <Formik<ServiceModalValues> initialValues={{ selectedServices: [] }} onSubmit={submitServices} enableReinitialize validationSchema={_prodValidate}>
          {({ isSubmitting, values, setFieldValue, errors }: { isSubmitting: boolean; values: any; setFieldValue: (name: string, val?: any) => void; errors: any }) => {
            const toggleService = (serviceId: number) => {
              const newSelected: number[] = values.selectedServices.includes(serviceId) ? values.selectedServices.filter((id: number) => id !== serviceId) : [...values.selectedServices, serviceId];
              setFieldValue("selectedServices", newSelected);
            };

            const toggleSelectAll = () => {
              if (values.selectedServices.length === filteredServices.length) {
                setFieldValue("selectedServices", []);
              } else {
                setFieldValue(
                  "selectedServices",
                  filteredServices.map((p) => p.id)
                );
              }
            };

            const filteredServices = React.useMemo(() => {
              const currentCombo = combinations.find((combo) => combo.id === selectedComboId);
              if (!currentCombo) return servicesList; // If no selected combo, show all services.
              const addedServiceIds = new Set(currentCombo.related_services.map((s) => s.service_id));

              return servicesList.filter((service) => !addedServiceIds.has(service.id) && service.name.toLowerCase().includes(searchTerm.toLowerCase()));
            }, [selectedComboId, combinations, searchTerm]);

            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => toggleServiceModal()}>
                  <ModalIcon>
                    <ServicesIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Add services</ModalTitle>
                    <ModalSubtitle>{"Add available services at this depot."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <SearchField value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} width="100%" />
                  <Flex sx={{ marginTop: "20px", marginBottom: "10px", alignItems: "center" }}>
                    <Checkbox label="Select All" checked={values.selectedServices.length === filteredServices.length && filteredServices.length > 0} onChange={toggleSelectAll} />
                  </Flex>
                  {filteredServices.map((service, idx) => (
                    <Flex
                      key={service.id}
                      sx={{
                        gap: "1rem",
                        alignItems: "center",
                        marginBottom: "10px",
                        border: `1px solid ${values.selectedServices.includes(service.id) ? "black" : themevals.colors.mdgrey}`,
                        borderRadius: "6px",
                        height: "38px",
                        padding: "0px 10px",
                      }}
                    >
                      <Checkbox label={service.name} checked={values.selectedServices.includes(service.id)} onChange={() => toggleService(service.id)} labelSize="14px" />
                    </Flex>
                  ))}
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondaryOutline" type="button" onClick={() => setServiceOpen(false)} style={{ marginRight: "10px" }}>
                    Cancel
                  </Button>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <PlusIcon fill={"#fff"} />}
                    Add services
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
      <Modal isOpen={newComboOpen} toggle={() => toggleNewComboModal()} centered size={"sm"}>
        <Formik<ComboModalValues> initialValues={{ bulk_product: null, selectedServices: [], selectedAddOnProducts: [] }} onSubmit={submitNewCombo} enableReinitialize validationSchema={comboSchema}>
          {({ isSubmitting, values, setFieldValue, errors }: { isSubmitting: boolean; values: any; setFieldValue: (name: string, val?: any) => void; errors: any }) => {
            const toggleAddOnProduct = (productId: number) => {
              const newSelected = values.selectedAddOnProducts.includes(productId)
                ? values.selectedAddOnProducts.filter((id: number) => id !== productId) // Remove if already selected
                : [...values.selectedAddOnProducts, productId]; // Add if not selected

              setFieldValue("selectedAddOnProducts", newSelected);
            };

            const toggleServices = (serviceId: number) => {
              const newSelected = values.selectedServices.includes(serviceId)
                ? values.selectedServices.filter((id: number) => id !== serviceId) // Remove if already selected
                : [...values.selectedServices, serviceId]; // Add if not selected

              setFieldValue("selectedServices", newSelected);
            };

            return (
              <Form noValidate autoComplete="off">
                <ModalHeader toggle={() => toggleNewComboModal()}>
                  <ModalIcon>
                    <ProductsIcon fill={"000"} />
                  </ModalIcon>
                  <ModalTitleWrapper>
                    <ModalTitle>Add product combination</ModalTitle>
                    <ModalSubtitle>{"Add product combinations for Instant Estimate."}</ModalSubtitle>
                  </ModalTitleWrapper>
                </ModalHeader>
                <ModalBody>
                  <SelectInput
                    name="bulk_product"
                    label="Bulk Product"
                    value={values.bulk_product}
                    options={bulkProductList}
                    onChange={(val: any) => setFieldValue("bulk_product", val)}
                    $errors={errors.bulk_product && submitCount > 0 ? errors.bulk_product : null}
                    $customStyles={{ flexGrow: 1, width: "100%" }}
                  />
                  <Subtext marginTop={"30px"} marginBottom={"8px"} fontFamily="Poppins">
                    Related Services
                  </Subtext>
                  {servicesList.map((service, idx) => (
                    <Flex
                      key={service.id}
                      sx={{
                        gap: "1rem",
                        alignItems: "center",
                        marginBottom: "10px",
                        border: `1px solid ${values.selectedServices.includes(service.id) ? "black" : themevals.colors.mdgrey}`,
                        borderRadius: "6px",
                        height: "38px",
                        padding: "0px 10px",
                      }}
                    >
                      <Checkbox label={service.name} checked={values.selectedServices.includes(service.id)} onChange={() => toggleServices(service.id)} labelSize="14px" />
                    </Flex>
                  ))}
                  {addOnProductList.length > 0 && (
                  <Subtext marginTop={"30px"} marginBottom={"8px"} fontFamily="Poppins">
                    Add-On Products
                  </Subtext>
                  )}
                  {addOnProductList.map((product, idx) => (
                    <Flex
                      key={product.id}
                      sx={{
                        gap: "1rem",
                        alignItems: "center",
                        marginBottom: "10px",
                        border: `1px solid ${values.selectedAddOnProducts.includes(product.id) ? "black" : themevals.colors.mdgrey}`,
                        borderRadius: "6px",
                        height: "38px",
                        padding: "0px 10px",
                      }}
                    >
                      <Checkbox label={product.name} checked={values.selectedAddOnProducts.includes(product.id)} onChange={() => toggleAddOnProduct(product.id)} labelSize="14px" />
                    </Flex>
                  ))}
                </ModalBody>
                <ModalFooter>
                  <Button variant="secondaryOutline" type="button" onClick={() => setNewComboOpen(false)} style={{ marginRight: "10px" }}>
                    Cancel
                  </Button>
                  <BasicButton type="submit" $submitting={isSubmitting} $iconHeight="14px" $iconWidth="14px" $loadingColor="#fff" keepText={true}>
                    {!isSubmitting && <PlusIcon fill={"#fff"} />}
                    Add combination
                  </BasicButton>
                </ModalFooter>
              </Form>
            );
          }}
        </Formik>
      </Modal>
    </>
  );
};

export default InstantEstimateSettings;
